'use client';
import React from 'react';
import styled from '@benzinga/themetron';
import { FAQ, QAProps } from '@benzinga/ui';
import { JsonLd } from '@benzinga/seo';

export interface FAQBlockProps {
  faq: QAProps[];
}

interface FAQchemaAnswer {
  '@type': string;
  text: string;
}

interface FAQchemaQuestion {
  '@type': string;
  acceptedAnswer: FAQchemaAnswer;
  name: string;
}

type FAQchema = {
  '@context': 'https://schema.org';
  '@type': 'FAQPage';
  mainEntity: FAQchemaQuestion[];
} & Record<string, unknown>;

export const FAQBlock: React.FC<FAQBlockProps> = ({ faq }) => {
  if (!faq) return <div />;

  const extractTextContent = (node: React.ReactNode): string => {
    if (typeof node === 'string') return node;
    if (typeof node === 'number') return node.toString();
    if (!node) return '';

    // For React elements, extract text content recursively
    if (React.isValidElement(node)) {
      const children = (node.props as { children?: React.ReactNode }).children;
      return React.Children.toArray(children)
        .map(child => extractTextContent(child))
        .join('');
    }

    // For arrays of children
    if (Array.isArray(node)) {
      return node.map(child => extractTextContent(child)).join('');
    }

    return '';
  };

  const formatSchemaData = (FAQData: QAProps[]): FAQchema => {
    const mappedQuestions = FAQData.map(item => {
      return {
        '@type': 'Question',
        acceptedAnswer: {
          '@type': 'Answer',
          text: extractTextContent(item?.answer),
        },
        name: item?.question,
      };
    });

    return {
      '@context': 'https://schema.org',
      '@type': 'FAQPage',
      mainEntity: mappedQuestions,
    };
  };

  return (
    <FAQWrapper className="block">
      <JsonLd data={formatSchemaData(faq)} id="faq-schema" />
      <FAQ faqData={faq} />
    </FAQWrapper>
  );
};

export const FAQWrapper = styled.div`
  margin: 1rem auto;
`;
